"use client";

import React from "react";
import { Eye } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 20,
  orderby: "date",
  order: "desc"
};
const wp_query = new WP_Query(wp_query_params);
function ProductGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="103277" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[450px] w-full animate-pulse rounded-sm border-[0.5px] border-border bg-muted/10" />)}
      </div>;
  }
  if (error) {
    return <div className="flex h-40 items-center justify-center rounded-sm border-[0.5px] border-primary/20 bg-primary/5 text-sm text-primary">
        Failed to load clinical standards. Please contact laboratory support.
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="flex h-40 items-center justify-center rounded-sm border-[0.5px] border-border bg-muted/5 text-sm text-muted-foreground">
        No research compounds currently in inventory.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-4">
      {products.map((product, i) => <div key={product.id} data-index={i} className="group relative flex w-full flex-col overflow-hidden rounded-sm border-[0.5px] border-border bg-white transition-all duration-300 hover:border-primary/30 hover:shadow-sm">
          <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-muted/10 p-8">
            <div className="absolute bottom-4 left-1/2 h-4 w-3/4 -translate-x-1/2 rounded-[100%] bg-black/5 blur-md" />
            
            <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617375/img/generic_vial_mockup.png"} alt={product.title} className="relative z-10 h-full w-full object-contain drop-shadow-sm transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" />

            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 opacity-0 backdrop-blur-[1px] transition-opacity duration-300 group-hover:opacity-100">
              <Button asChild variant="secondary" className="rounded-sm border-border bg-white text-foreground shadow-sm hover:bg-muted">
                <Link to={product.link}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Technical Specs
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex flex-grow flex-col gap-4 p-6">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-sans text-[18px] font-medium leading-tight tracking-tight text-foreground">
                <Link to={product.link} className="transition-colors hover:text-primary">
                  {product.title}
                </Link>
              </h3>
              <Badge variant="outline" className="shrink-0 border-primary/20 bg-primary/5 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-primary">
                ≥99% Purity
              </Badge>
            </div>

            <div className="mt-auto flex items-end justify-between border-t-[0.5px] border-border pt-4">
              <div className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Batch Protocol
                </span>
                <span className="text-xs font-medium text-foreground">
                  Lyophilized Vial
                </span>
              </div>
              <div className="text-right">
                <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Unit Price
                </span>
                <span className="text-lg font-medium text-foreground">
                  ${product.price?.toFixed(2) || "0.00"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 pt-0">
            <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-sm font-medium tracking-wide transition-all" text="Add to Requisition" showQuantity={false} />
          </div>
        </div>)}
    </div>;
}
export default function FeaturedResearchPeptides() {
  return <section className="bg-background py-6 px-4 md:px-6 lg:py-10">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <h2 className="font-sans text-2xl font-medium tracking-tight text-foreground md:text-3xl">Featured Research Peptides</h2>
          <div className="mt-4 h-[1px] w-16 bg-primary" />
        </div>

        <div className="mb-12 flex flex-col items-stretch gap-6 overflow-hidden rounded-sm border border-border bg-card transition-all duration-300 hover:border-primary/20 md:flex-row">
          <div className="flex flex-1 flex-col justify-center p-4 md:p-6 lg:p-10">
            <h3 className="mb-6 font-sans text-lg font-medium tracking-tight text-foreground md:text-xl">
              Premium Research Catalog
            </h3>
            <p className="mb-10 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Our precision-engineered compounds are synthesized to exceed 99% purity standards. Each batch undergoes rigorous third-party validation to ensure absolute consistency for your laboratory requirements.
            </p>
            <Button asChild size="default" className="w-fit rounded-sm bg-primary px-8 py-4 text-base font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-lg">
              <Link to="/ShopPage">Browse Compounds</Link>
            </Button>
          </div>
          <div className="relative flex-1 max-h-[400px] overflow-hidden bg-muted/20">
            <video autoPlay muted loop className="h-full w-full object-cover grayscale-[0.1] transition-transform duration-700 hover:scale-105 hover:grayscale-0">
              <source src="https://signalsourceone.com/wp-content/uploads/2026/05/hf_20260504_022457_399fc429-735f-4717-8bb2-6f7dd7a592de.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 20,
        orderby: "date",
        order: "desc"
      }}>
          <ProductGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}